import React, { useCallback, useEffect, useState } from "react";
import Navbar from "@/components/Navbar";
import { api } from "@/lib/api";
import { toast } from "sonner";
import { Bell, Mail, MessageCircle, Smartphone, Send, Shield, AlertTriangle, TestTube2, Phone } from "lucide-react";
import { subscribeToPush, unsubscribeFromPush, ensurePushSw } from "@/lib/push";


const EVENT_LABELS = {
  trade_entered: { label: "Trade entered", desc: "A live deployment placed an order." },
  trade_closed: { label: "Trade closed", desc: "An open position hit SL, TP, or was closed manually." },
  deployment_error: { label: "Deployment errors", desc: "Broker or strategy errors that pause your bot." },
  drift: { label: "Backtest-vs-live drift", desc: "Live results diverge significantly from backtest." },
  emotion_pattern: { label: "Emotion pattern", desc: "Your recent losses share a mistake tag or emotion." },
  pro_milestone: { label: "Pro plan milestones", desc: "Trial-ending nudges, usage recaps, upgrades." },
};

const CHANNEL_ICONS = {
  inapp: <Bell size={15}/>,
  email: <Mail size={15}/>,
  telegram: <MessageCircle size={15}/>,
  sms: <Phone size={15}/>,
  push: <Smartphone size={15}/>,
  native: <Smartphone size={15}/>,
};


export default function NotificationSettings() {
  const [prefs, setPrefs] = useState(null);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [pushBusy, setPushBusy] = useState(false);
  const [testBusy, setTestBusy] = useState(false);

  const load = useCallback(async () => {
    try {
      const { data } = await api.get("/notifications/prefs");
      setPrefs(data);
    } catch { toast.error("Failed to load preferences"); }
    finally { setLoading(false); }
  }, []);
  useEffect(() => { load(); }, [load]);

  const save = async (patch) => {
    setSaving(true);
    try {
      const { data } = await api.patch("/notifications/prefs", patch);
      setPrefs(data);
    } catch (e) { toast.error(e?.response?.data?.detail || "Save failed"); }
    finally { setSaving(false); }
  };

  const toggleChannel = (ch, val) => save({ channels: { ...prefs.channels, [ch]: val } });
  const toggleEvent = (ev, val) => save({ events: { ...prefs.events, [ev]: val } });

  const enablePush = async () => {
    setPushBusy(true);
    try {
      const ok = await subscribeToPush(prefs?.vapid_public_key);
      if (ok) {
        toast.success("Browser push enabled");
        await save({ channels: { ...prefs.channels, push: true } });
      }
    } finally { setPushBusy(false); }
  };
  const disablePush = async () => {
    setPushBusy(true);
    try { await unsubscribeFromPush(); toast.success("Push disabled on this device"); load(); }
    finally { setPushBusy(false); }
  };

  const sendTest = async () => {
    setTestBusy(true);
    try {
      const { data } = await api.post("/notifications/test");
      const delivered = Object.entries(data.delivered || {}).filter(([k, v]) => v).map(([k]) => k);
      if (delivered.length) toast.success("Test sent via: " + delivered.join(", "));
      else toast.info("Test written to in-app only — no other channels configured yet");
    } catch (e) { toast.error(e?.response?.data?.detail || "Test failed"); }
    finally { setTestBusy(false); }
  };

  if (loading) return <div className="min-h-screen"><Navbar/><div className="p-10 text-[var(--text-muted)] font-mono">Loading…</div></div>;
  if (!prefs) return null;

  const available = prefs.available_channels || {};

  return (
    <div className="min-h-screen bg-[var(--bg-dark)]" data-testid="notification-settings-page">
      <Navbar/>
      <main className="max-w-3xl mx-auto px-6 py-10">
        <header className="mb-8">
          <div className="chip mb-3"><Shield size={11}/> ACCOUNT</div>
          <h1 className="font-display text-4xl text-white">Notifications</h1>
          <p className="text-[var(--text-muted)] mt-2 text-sm max-w-2xl">
            Choose <strong>where</strong> we contact you (channels) and <strong>when</strong> (events).
            You can silence everything except in-app notifications; the bell will always keep a log.
          </p>
        </header>

        <section className="card-sharp p-6 mb-6" data-testid="notif-channels">
          <h2 className="font-display text-xl text-white mb-4">Delivery channels</h2>
          <div className="space-y-4">
            {["inapp","email","telegram","sms","push","native"].map(ch => (
              <div key={ch} className="flex items-center gap-3 border-b border-white/5 pb-3 last:border-0 last:pb-0"
                data-testid={`ch-row-${ch}`}>
                <div className="w-8 h-8 rounded flex items-center justify-center bg-white/5 text-[var(--accent)]">
                  {CHANNEL_ICONS[ch]}
                </div>
                <div className="flex-1 min-w-0">
                  <div className="text-white font-display capitalize">{
                    ch === "inapp" ? "In-app notifications" :
                    ch === "email" ? "Email" :
                    ch === "telegram" ? "Telegram" :
                    ch === "sms" ? "SMS (Twilio)" :
                    ch === "native" ? "Mobile app (iOS/Android)" :
                    "Browser push"
                  }</div>
                  <div className="text-[11px] font-mono text-[var(--text-muted)]">
                    {ch === "inapp" && "Always available. The bell icon in the top-right shows unread."}
                    {ch === "email" && (available.email ? "Delivered via Resend to your account email." : "⚠ Not configured — set RESEND_API_KEY on the server.")}
                    {ch === "telegram" && (available.telegram ? "Two-way commands: /status /pause /resume. Link chat via one-time token below." : "⚠ Not configured — set TELEGRAM_BOT_TOKEN on the server.")}
                    {ch === "sms" && (available.sms ? "Critical alerts only (deployment errors, drift, entries) via Twilio." : "⚠ Not configured — set TWILIO_ACCOUNT_SID + TWILIO_AUTH_TOKEN + TWILIO_FROM_NUMBER.")}
                    {ch === "push" && (available.push ? `${prefs.push_subscription_count} device(s) subscribed.` : "⚠ Not configured — set VAPID keys on the server.")}
                    {ch === "native" && (available.native ? `${prefs.native_push_token_count || 0} mobile device(s) registered. Open the iOS/Android app to grant permission.` : "⚠ Not configured — set FIREBASE_SERVICE_ACCOUNT_JSON on the server (see MOBILE_APP.md).")}
                  </div>
                </div>
                {ch === "push" ? (
                  prefs.channels.push && prefs.push_subscription_count > 0 ? (
                    <button data-testid={`ch-disable-${ch}`} onClick={disablePush} disabled={pushBusy}
                      className="btn-outline text-xs">Disable this device</button>
                  ) : (
                    <button data-testid={`ch-enable-${ch}`} onClick={enablePush} disabled={pushBusy || !available.push}
                      className="btn-primary text-xs">{pushBusy ? "…" : "Enable on this device"}</button>
                  )
                ) : (
                  <Toggle testId={`ch-toggle-${ch}`}
                    checked={!!prefs.channels[ch]}
                    onChange={(v)=>toggleChannel(ch, v)}
                    disabled={saving || ch !== "inapp" && !available[ch]}/>
                )}
              </div>
            ))}
          </div>

          {prefs.channels.telegram && available.telegram && (
            <div className="mt-4 p-3 border border-white/10 rounded bg-black/30" data-testid="telegram-setup">
              <label className="label-tiny">Link this account to Telegram</label>
              <TelegramLinkTokenButton/>
              <p className="text-[10px] font-mono text-[var(--text-muted)] mt-2 leading-relaxed">
                Legacy: paste your chat ID directly (skip if you use /link)
              </p>
              <div className="flex gap-2 mt-1">
                <input data-testid="telegram-chat-id" type="text"
                  className="input-dark flex-1 !text-xs"
                  placeholder="e.g. 123456789"
                  defaultValue={prefs.telegram_chat_id || ""}
                  onBlur={(e)=>save({ telegram_chat_id: e.target.value })}/>
              </div>
            </div>
          )}

          {prefs.channels.sms && (
            <div className="mt-4 p-3 border border-white/10 rounded bg-black/30" data-testid="sms-setup">
              <label className="label-tiny">Phone number (E.164 format)</label>
              <input data-testid="sms-phone-number" type="tel"
                className="input-dark w-full !text-xs mt-1"
                placeholder="+14155552671"
                defaultValue={prefs.phone_number || ""}
                onBlur={(e)=>save({ phone_number: e.target.value })}/>
              <label className="flex items-center gap-2 mt-3 text-xs text-[var(--text-secondary)] cursor-pointer">
                <input data-testid="sms-critical-only" type="checkbox"
                  checked={prefs.sms_critical_only ?? true}
                  onChange={(e)=>save({ sms_critical_only: e.target.checked })}
                  className="accent-[var(--accent)]"/>
                Critical events only (deployment errors, drift, entries) — recommended
              </label>
              <p className="text-[10px] font-mono text-[var(--text-muted)] mt-1.5">
                SMS costs money. Uncheck to receive every event. E.164 format includes + and country code.
              </p>
            </div>
          )}
        </section>

        <section className="card-sharp p-6 mb-6" data-testid="notif-events">
          <h2 className="font-display text-xl text-white mb-4">Events</h2>
          <p className="text-[11px] font-mono text-[var(--text-muted)] mb-4">
            Toggle which events fire notifications. Applies to ALL enabled channels above.
          </p>
          <div className="space-y-3">
            {Object.entries(EVENT_LABELS).map(([ev, meta]) => (
              <div key={ev} className="flex items-center gap-3 border-b border-white/5 pb-3 last:border-0 last:pb-0"
                data-testid={`ev-row-${ev}`}>
                <div className="flex-1 min-w-0">
                  <div className="text-white font-display text-sm">{meta.label}</div>
                  <div className="text-[11px] font-mono text-[var(--text-muted)]">{meta.desc}</div>
                </div>
                <Toggle testId={`ev-toggle-${ev}`}
                  checked={!!prefs.events[ev]}
                  onChange={(v)=>toggleEvent(ev, v)} disabled={saving}/>
              </div>
            ))}
          </div>
        </section>

        <section className="card-sharp p-6" data-testid="notif-test-section">
          <div className="flex items-start gap-4">
            <div className="flex-1">
              <h2 className="font-display text-xl text-white flex items-center gap-2 mb-2">
                <TestTube2 size={18} className="text-[var(--accent)]"/> Send a test
              </h2>
              <p className="text-[11px] font-mono text-[var(--text-muted)] leading-relaxed">
                Fires a "Test notification" through every enabled channel above. Use this after enabling a new channel to verify it works.
              </p>
            </div>
            <button data-testid="notif-test-btn" onClick={sendTest} disabled={testBusy}
              className="btn-primary flex items-center gap-2 shrink-0">
              <Send size={13}/> {testBusy ? "Sending…" : "Send test"}
            </button>
          </div>
        </section>

        {!available.email && !available.telegram && !available.push && (
          <div className="mt-6 border border-[var(--warning)]/30 bg-[var(--warning)]/5 rounded p-4 flex gap-3"
            data-testid="notif-no-external">
            <AlertTriangle size={16} className="text-[var(--warning)] mt-0.5 shrink-0"/>
            <div className="text-[11px] font-mono text-[var(--warning)] leading-relaxed">
              Only in-app notifications are available right now. To enable email / Telegram / browser push, an admin must configure server-side keys (<code>RESEND_API_KEY</code>, <code>TELEGRAM_BOT_TOKEN</code>, <code>VAPID_PUBLIC_KEY</code>/<code>VAPID_PRIVATE_KEY</code>). Your prefs are saved and will activate automatically once those are set.
            </div>
          </div>
        )}
      </main>
    </div>
  );
}


function Toggle({ checked, onChange, disabled, testId }) {
  return (
    <button data-testid={testId}
      onClick={()=>onChange(!checked)} disabled={disabled}
      className={`relative w-10 h-5 rounded-full transition-colors ${checked ? "bg-[var(--accent)]" : "bg-white/10"} ${disabled ? "opacity-50 cursor-not-allowed" : ""}`}>
      <span className={`absolute top-0.5 w-4 h-4 rounded-full transition-transform bg-black ${checked ? "translate-x-[22px]" : "translate-x-0.5"}`}/>
    </button>
  );
}



function TelegramLinkTokenButton() {
  const [token, setToken] = React.useState(null);
  const [busy, setBusy] = React.useState(false);
  const generate = async () => {
    setBusy(true);
    try {
      const { data } = await api.post("/telegram/link-token");
      setToken(data.token);
    } catch (err) { console.debug("[telegram] link-token generate failed", err); }
    finally { setBusy(false); }
  };
  return (
    <div className="flex flex-col gap-2 mt-1">
      <button data-testid="telegram-link-generate" onClick={generate} disabled={busy}
        className="btn-outline text-xs !py-1 self-start">
        {busy ? "Generating…" : token ? "Regenerate token" : "Generate link token"}
      </button>
      {token && (
        <div className="p-2 border border-[var(--accent)]/30 bg-black/30 rounded" data-testid="telegram-link-token-value">
          <p className="text-[10px] font-mono text-[var(--text-muted)]">Send this to your bot:</p>
          <code className="text-[var(--accent)] font-mono text-sm">/link {token}</code>
          <p className="text-[10px] font-mono text-[var(--text-muted)] mt-1">Expires in 15 min. One-time use.</p>
        </div>
      )}
    </div>
  );
}
